import { LogoutOutlined } from '@ant-design/icons'
import { Button, Layout, Space, Typography, theme } from 'antd'
import type { CSSProperties, ReactNode } from 'react'

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
  height: 64,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}

/**
 * The top bar of a backoffice: app title on the left, account and actions on the right.
 *
 * On a wide screen it renders what each backoffice header rendered before. The narrow-screen
 * behaviour (room for the menu button, one-line title, shortened account name, icon-only logout)
 * is in `responsive.css`, keyed by the `bo-header*` class names set here.
 */
export function HeaderBar({
  title,
  children,
}: {
  title: ReactNode
  children: ReactNode
}): React.JSX.Element {
  const { token } = theme.useToken()
  return (
    <Layout.Header
      className="bo-header"
      style={{ ...headerStyle, background: token.colorBgElevated }}
    >
      <Typography.Text strong className="bo-header__title" style={{ fontSize: 18 }}>
        {title}
      </Typography.Text>
      <Space size="middle" align="center" className="bo-header__actions">
        {children}
      </Space>
    </Layout.Header>
  )
}

/** The signed-in account's name; cut with an ellipsis on a phone instead of breaking per letter. */
export function HeaderUserName({ children }: { children: ReactNode }): React.JSX.Element {
  return <Typography.Text className="bo-header__user-name">{children}</Typography.Text>
}

/** Logout button; on a phone only the icon is shown (the label stays as the accessible name). */
export function HeaderLogoutButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}): React.JSX.Element {
  return (
    <Button
      data-testid="header-logout-btn"
      type="text"
      icon={<LogoutOutlined />}
      aria-label={label}
      onClick={onClick}
    >
      <span className="bo-header__action-label">{label}</span>
    </Button>
  )
}
